import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core'
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms'
import { MatButton } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatDividerModule } from '@angular/material/divider'
import { MatExpansionModule } from '@angular/material/expansion'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInput } from '@angular/material/input'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { NgxMaskDirective } from 'ngx-mask'
import { forkJoin, of } from 'rxjs'
import { catchError } from 'rxjs/operators'
import { ErrorRequestModel, ResponseRequestModel } from '../../../core/models/response.model'
import { CreateSupplierFormModel } from '../../../core/models/supplier.model'
import { AddressService } from '../../../core/services/address/address/address.service'
import { BankDetailsService } from '../../../core/services/bank/bank-details/bank-details.service'
import { ContactService } from '../../../core/services/contact/contact/contact.service'
import { PaymentMethodsService } from '../../../core/services/payment/payment-methods/payment-methods.service'
import { SupplierService } from '../../../core/services/person/supplier/supplier.service'
import { TaxDocumentTypeService } from '../../../core/services/tax-document/tax-document-type/tax-document-type.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { SupplierVehicleTypeComponent } from '../../person/supplier/supplier-vehicle-type/supplier-vehicle-type.component'

@Component({
  selector: 'client-convert-supplier',
  imports: [
    ReactiveFormsModule,
    MatButton,
    MatProgressSpinner,
    MatExpansionModule,
    MatFormFieldModule,
    MatCardModule,
    MatInput,
    MatDividerModule,
    NgxMaskDirective,
    SupplierVehicleTypeComponent,
  ],
  templateUrl: './client-convert-supplier.component.html',
  styleUrl: './client-convert-supplier.component.scss',
  providers: [
    SupplierService,
    AddressService,
    ContactService,
    TaxDocumentTypeService,
    PaymentMethodsService,
    BankDetailsService,
  ],
  standalone: true,
})
export class ClientConvertSupplierComponent implements OnInit {
  constructor(
    private formBuilder: FormBuilder,
    private supplierService: SupplierService,
    private addressService: AddressService,
    private contactService: ContactService,
    private taxDocumentTypeService: TaxDocumentTypeService,
    private paymentMethodsService: PaymentMethodsService,
    private bankDetailsService: BankDetailsService,
    private snackbar: SnackbarCustomService,
    private dialogRef: MatDialogRef<ClientConvertSupplierComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.item = data.item
  }

  @Output() response = new EventEmitter()

  formLegalEntity: FormGroup = new FormGroup({})
  formSupplier: FormGroup = new FormGroup({})

  item: any
  loading = false
  loadingScreen = true

  panelVehicleType = false

  vehicleTypeList: any[] = []

  // Dados da pessoa do cliente. O back os descarta (a pessoa e reaproveitada), mas o validator
  // do create/from-cliente ainda exige as listas preenchidas, entao eles vao no payload.
  addressList: any[] = []
  contactList: any[] = []
  taxDocumentList: any[] = []
  paymentMethodsList: any[] = []
  bankDetailsList: any[] = []

  ngOnInit(): void {
    this.createForm()
    this.getPersonData()
  }

  close() {
    this.dialogRef.close()
  }

  createForm(): void {
    this.formLegalEntity = this.formBuilder.group({
      name: [this.item.nomeFantasia, [Validators.required]],
      cnpj: [this.item.cnpj, [Validators.required]],
      corporate_name: [this.item.razaoSocial, [Validators.required]],
      reg_state: [this.item.inscricaoEstadual],
      reg_municipal: [this.item.inscricaoMunicipal],
    })
    this.formSupplier = this.formBuilder.group({
      prazoPagamento: [null],
    })
  }

  getPersonData(): void {
    const pessoaId = this.item.pessoaId
    forkJoin({
      address: this.addressService.listAddressByPerson(pessoaId).pipe(catchError(() => of(null))),
      contact: this.contactService.listContactByPerson(pessoaId).pipe(catchError(() => of(null))),
      taxDocument: this.taxDocumentTypeService.listTaxDocumentByPerson(pessoaId).pipe(catchError(() => of(null))),
      paymentMethods: this.paymentMethodsService.listPaymentMethodsByPerson(pessoaId).pipe(catchError(() => of(null))),
      bankDetails: this.bankDetailsService.listBankDetailsByPerson(pessoaId).pipe(catchError(() => of(null))),
    }).subscribe({
      next: (response: any) => {
        this.addressList = this.treatList(response.address)
        this.contactList = this.treatList(response.contact)
        this.taxDocumentList = this.treatList(response.taxDocument)
        this.paymentMethodsList = this.treatList(response.paymentMethods)
        this.bankDetailsList = this.treatList(response.bankDetails)
      },
      error: (err: ErrorRequestModel) => {
        console.error('err', err)
        this.loadingScreen = false
      },
      complete: () => {
        this.loadingScreen = false
      },
    })
  }

  treatList(response: ResponseRequestModel | null): any[] {
    return response && Array.isArray(response.data) ? response.data : []
  }

  getList(list: any[], typeList: 'vehicleType'): void {
    switch (typeList) {
      case 'vehicleType':
        this.vehicleTypeList = list
        break
    }
  }

  checkInvalidForm() {
    return !this.loading
  }

  /**
   * Endereco, contato, documento fiscal, forma de pagamento e dados bancarios sao dados da
   * pessoa: o back reaproveita os que ja existem e descarta os enviados aqui. Mas o validator
   * do create/from-cliente exige as cinco listas nao-vazias, entao quando o cliente nao tem o
   * bloco vai um item de reserva so para passar na validacao.
   *
   * As listagens por pessoa devolvem tipoEnderecoId/tipoPagamentoId/bancoId, enquanto o DTO do
   * cadastro espera tipo_endereco_id/tipo_pagamento_id/banco — e os tres sao int nao-anulavel,
   * entao o campo faltando vira string vazia e o model binding do back reprova com 400.
   */
  createModel() {
    const model = new CreateSupplierFormModel()

    model.fornecedor = {
      nome: this.formLegalEntity.get('name')?.value ?? '',
      razao_social: this.formLegalEntity.get('corporate_name')?.value ?? '',
      insc_municipal: this.formLegalEntity.get('reg_municipal')?.value ?? '',
      insc_estadual: this.formLegalEntity.get('reg_state')?.value ?? '',
      cnpj: this.formLegalEntity.get('cnpj')?.value ?? '',
      prazoPagamento: this.formSupplier.get('prazoPagamento')?.value
        ? Number(this.formSupplier.get('prazoPagamento')?.value)
        : undefined,
      tiposVeiculoId: this.vehicleTypeList.map((ele: any) => ele.id),
    }

    model.enderecos = this.fillWhenEmpty(
      this.addressList.map((ele: any) => ({
        tipo_endereco_id: this.toId(ele.tipoEnderecoId),
        cep: ele.cep ?? '',
        logradouro: ele.logradouro ?? '',
        numero: ele.numero ?? '',
        complemento: ele.complemento ?? '',
        bairro: ele.bairro ?? '',
        estado: ele.estado ?? '',
        cidade: ele.cidade ?? '',
        pais: ele.pais ?? 'Brasil',
      })),
      { tipo_endereco_id: 0 },
    )
    // toFormData so serializa o contato que tem tipo e descricao, entao o item de reserva
    // precisa vir preenchido, senao a lista chega nula no back e o validator reprova.
    model.contatos = this.fillWhenEmpty(
      this.contactList.map((ele: any) => ({
        tipoContatoId: this.toId(ele.tipoContatoId),
        descricao: ele.descricao ?? '-',
      })),
      { tipoContatoId: 0, descricao: '-' },
    )
    model.documento_fiscal = this.taxDocumentList.length
      ? this.taxDocumentList.map((ele: any) => this.toId(ele.tipoDocFiscalId))
      : [0]
    model.forma_pagamento = this.fillWhenEmpty(
      this.paymentMethodsList.map((ele: any) => ({
        tipo_pagamento_id: this.toId(ele.tipoPagamentoId),
        prazo: ele.prazo ?? '',
      })),
      { tipo_pagamento_id: 0, prazo: '' },
    )
    model.dados_bancarios = this.fillWhenEmpty(
      this.bankDetailsList.map((ele: any) => ({
        banco: this.toId(ele.bancoId),
        agencia: ele.agencia ?? '',
        conta: ele.conta ?? '',
        pix: ele.pix ?? '',
      })),
      { banco: 0, agencia: '', conta: '', pix: '' },
    )
    model.responsaveis = []

    return model.toFormData()
  }

  toId(value: any): number {
    return Number(value) || 0
  }

  fillWhenEmpty(list: any[], placeholder: any): any[] {
    return list.length > 0 ? list : [placeholder]
  }

  transformSupplier() {
    this.loading = true
    this.supplierService.createSupplierFromClient(this.createModel()).subscribe({
      next: (response: ResponseRequestModel) => {
        this.snackbar.success(response.message)
        this.response.emit(true)
        this.close()
      },
      error: (err: ErrorRequestModel) => {
        this.loading = false
        this.snackbar.errorRequest(err)
      },
      complete: () => {
        this.loading = false
      },
    })
  }
}
